import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { DiscoverTabs, ContentTypes, UrlSearchParams } from "types/enums";

import SignIn from "./common/pages/sign-in.tsx";
import SignUp from "./common/pages/sign-up.tsx";
import AutoStore from "./common/pages/autostore.tsx";
import SignUpViaCompanyMagicLink from "./common/pages/sign-up-via-company-magic-link";
import InvalidLink from "./common/pages/sign-up-via-company-magic-link/invalid-link";
import Corporate from "pages/corporate/index";
import Users from "./learner/index";
import Onboard, {
  Profile,
  CalendarInvites,
  OnboardingSuccess,
} from "apps/user/common/pages/onboard";
import {
  isOnboardingPath,
  shouldRedirectOutOfOnboarding,
} from "apps/user/common/pages/onboard/onboarding-access";
import { PageLoading } from "apps/user/learner/components/page-container";
import BootError from "apps/user/common/components/boot-error";
import Assessment from "apps/user/learner/pages/assessment";
import AssessmentInterstitialAnimation from "apps/user/learner/pages/assessment/assessment-interstitial-animation";
import AssessmentSubmissionError from "apps/user/learner/pages/assessment/assessment-submission-error";
import AssessmentProvider from "providers/Assessment";
import AssessmentReport from "apps/user/learner/pages/report";

import GetStarted from "apps/user/common/pages/get-started.tsx";
import AuthCallback from "apps/user/common/pages/auth-callback.tsx";
import EmailEntry from "apps/user/common/pages/auth/email-entry.tsx";
import MethodChooser from "apps/user/common/pages/auth/method-chooser.tsx";
import PasswordScreen from "apps/user/common/pages/auth/password-screen.tsx";
import SignInOtpCode from "apps/user/common/pages/auth/signin-otp-code.tsx";
import SignUpOtpCode from "apps/user/common/pages/auth/signup-otp-code.tsx";
import { useAuth } from "providers/auth";

import PageProvider from "providers/page";
import useQueryParams from "components/commons/hooks/useQueryParams";
import Results from "apps/user/common/pages/results.tsx";

import "styles/typography.scss";

// WONE-612 route safety: when the user is already authenticated and lands
// on an auth-flow entry screen, bounce them to the home redirect. Matches
// the mobile (auth)/_layout.tsx behaviour ("if learner resolves, redirect
// to /user/home"). Deliberately excludes:
//   - /auth/callback         — mid-SSO; needs the code exchange to run
//   - /signin/create         — token redeem; the screen logs out on mount
//   - /signin/reset          — legitimate "forgot password" while signed in
//   - /signin/autostore      — corporate-specific entry surface
//   - /signup/company*       — company magic-link signup (separate vertical)
const AUTHED_REDIRECT_FROM = new Set([
  "/login",
  "/signin",
  "/signup",
  "/getstarted",
  "/get-started",
  "/email-entry",
  "/method-chooser",
  "/signin/email-otp/code",
  "/signup/email-otp/code",
  "/signin/email",
  "/signin/magiclink",
]);

const UserApp = () => {
  // WONE-565 (Phase 4b): wait for the initial cookie-refresh probe to
  // settle before deciding whether the user is signed in. ``bootStatus``
  // is tri-state: "pending" while the probe runs, "ready" once it
  // resolved a definitive signed-in/signed-out state, or "error" when it
  // failed transiently (network/CORS/5xx/timeout) and needs a
  // human-triggered retry rather than sitting on PageLoading forever.
  const { user, companyAdmin, bootStatus, retryBootstrap } = useAuth();
  const authed = localStorage.getItem("Authed");
  const isStudent = localStorage.getItem("WOEstudentUserId");
  const shouldOnboard = localStorage.getItem("shouldOnboard");
  const shouldViewReadyScreen = localStorage.getItem("shouldViewReadyScreen");

  const query = useQueryParams();
  const location = useLocation();
  let { pathname } = location;

  // Normalize URL parameters: convert snake_case to camelCase
  const snakeCaseContentType = query.get(UrlSearchParams.ContentTypeSnakeCase);
  const snakeCaseContentId = query.get(UrlSearchParams.ContentIdSnakeCase);
  const snakeCaseSessionType = query.get(UrlSearchParams.SessionTypeSnakeCase);
  const snakeCaseSessionId = query.get(UrlSearchParams.SessionIdSnakeCase);

  const hasSnakeCaseParams =
    snakeCaseContentType ||
    snakeCaseContentId ||
    snakeCaseSessionType ||
    snakeCaseSessionId;

  if (hasSnakeCaseParams) {
    const newParams = new URLSearchParams(query.toString());

    // Convert content_type → contentType
    if (snakeCaseContentType) {
      newParams.delete(UrlSearchParams.ContentTypeSnakeCase);
      newParams.set(UrlSearchParams.ResourceType, snakeCaseContentType);
    }

    // Convert content_id → contentId
    if (snakeCaseContentId) {
      newParams.delete(UrlSearchParams.ContentIdSnakeCase);
      newParams.set(UrlSearchParams.ResourceId, snakeCaseContentId);
    }

    // Convert session_type=GROUP + session_id → contentType=session + contentId
    if (snakeCaseSessionType?.toLowerCase() === "group" && snakeCaseSessionId) {
      newParams.delete(UrlSearchParams.SessionTypeSnakeCase);
      newParams.delete(UrlSearchParams.SessionIdSnakeCase);
      newParams.set(UrlSearchParams.ResourceType, ContentTypes.SESSION);
      newParams.set(UrlSearchParams.ResourceId, snakeCaseSessionId);
    }

    // Replace URL with normalized parameters (stay on same route)
    return <Navigate to={`${pathname}?${newParams.toString()}`} replace />;
  }

  // Extract content parameters (now guaranteed to be camelCase)
  const contentType = query.get(UrlSearchParams.ResourceType);
  const contentId = query.get(UrlSearchParams.ResourceId);

  // Wait for the initial cookie-refresh probe before deciding. After
  // it lands, ``authed`` is either truthy (session restored) or null
  // (stale flag cleared by refreshAccessToken on failure).
  if (bootStatus === "pending") {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        // WONE-612: cover the global PatternBackground canvas with the
        // branded cream so the bootstrap spinner doesn't flash blue
        // before the auth screens (or the home screen) render.
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "#F4EFEA",
          zIndex: 1,
        }}
      >
        <PageLoading />
      </div>
    );
  }
  // The boot probe failed transiently (network/CORS/5xx or a bounded
  // timeout, retries exhausted) — surface a retry rather than leaving
  // the ``authed && !user`` case below stuck on PageLoading forever,
  // since nothing else re-drives the probe. Mirrors the mobile
  // Error screen (offline-vs-generic copy + a single retry).
  if (bootStatus === "error") {
    return <BootError onRetry={retryBootstrap} />;
  }
  if (authed && !user) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        // WONE-612: cover the global PatternBackground canvas with the
        // branded cream so the bootstrap spinner doesn't flash blue
        // before the auth screens (or the home screen) render.
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "#F4EFEA",
          zIndex: 1,
        }}
      >
        <PageLoading />
      </div>
    );
  } else if (!authed && pathname === "/") {
    // WONE-471: unauthenticated learner lands on the branded intro
    // (video bg + "Unlock peak potential. Every day." + Continue CTA).
    return <Navigate to={`/get-started${query ? `?${query}` : ""}`} />;
  } else if (
    !authed &&
    pathname !== "/get-started" &&
    pathname !== "/getstarted" &&
    pathname !== "/login" &&
    pathname !== "/signin" &&
    pathname !== "/signin/email" &&
    pathname !== "/signin/create" &&
    pathname !== "/signin/create-account" &&
    pathname !== "/signin/autostore" &&
    pathname !== "/signin/reset" &&
    pathname !== "/signin/magiclink" &&
    pathname !== "/signin/not-eligable" &&
    pathname !== "/signup" &&
    pathname !== "/signup/company" &&
    pathname !== "/signup/company/invalid-link" &&
    pathname !== "/auth/callback" &&
    pathname !== "/email-entry" &&
    pathname !== "/method-chooser" &&
    pathname !== "/signin/email-otp/code" &&
    pathname !== "/signup/email-otp/code"
  ) {
    return (
      <Navigate
        to={`/signin${query ? `?${query}` : ""}`}
        state={{ from: location }}
      />
    );
  } else if (
    authed &&
    shouldOnboard &&
    !isOnboardingPath(pathname) &&
    !pathname.startsWith("/signin")
  ) {
    return (
      <Navigate
        to={`/user/onboard${query ? `?${query}` : ""}`}
        state={{ from: location }}
      />
    );
  } else if (
    authed &&
    shouldRedirectOutOfOnboarding({
      pathname,
      shouldOnboard: Boolean(shouldOnboard),
      shouldViewReadyScreen: Boolean(shouldViewReadyScreen),
    })
  ) {
    // Replace rather than push: the entry being left is one the learner got
    // to with the back button, so pushing would bounce them straight forward
    // again on their next press.
    return <Navigate to={"/"} replace state={{ from: location }} />;
  } else if (authed && isStudent && pathname === "/") {
    return <Navigate to={`/user/home${query ? `?${query}` : ""}`} replace />;
  } else if (
    authed &&
    (pathname === "/user/session" || pathname.startsWith("/user/session/"))
  ) {
    pathname = pathname.replace("/user/session", "/user/sessions");
    return <Navigate to={`${pathname}${query ? `?${query}` : ""}`} />;
  } else if (authed && pathname.startsWith("/user/sessions/community/")) {
    pathname = pathname.replace(
      "/user/sessions/community/",
      "/user/sessions/group/"
    );
    return <Navigate to={`${pathname}${query ? `?${query}` : ""}`} />;
  } else if (authed && pathname.startsWith("/user/sessions/company/")) {
    pathname = pathname.replace(
      "/user/sessions/company/",
      "/user/sessions/group/"
    );
    return <Navigate to={`${pathname}${query ? `?${query}` : ""}`} />;
  } else if (authed && pathname.startsWith("/user/schedule/community/")) {
    pathname = pathname.replace(
      "/user/schedule/community/",
      "/user/schedule/group/"
    );
    return <Navigate to={`${pathname}${query ? `?${query}` : ""}`} />;
  } else if (authed && pathname.startsWith("/user/schedule/company/")) {
    pathname = pathname.replace(
      "/user/schedule/company/",
      "/user/schedule/group/"
    );
    return <Navigate to={`${pathname}${query ? `?${query}` : ""}`} />;
  } else if (
    authed &&
    (pathname === "/user/favourite" || pathname.startsWith("/user/favourite/"))
  ) {
    pathname = pathname.replace("/user/favourite", "/user/favourites");
    return <Navigate to={`${pathname}${query ? `?${query}` : ""}`} />;
  } else if (
    pathname.startsWith("/sessionLink") &&
    contentType &&
    contentId !== undefined
  ) {
    return (
      <Navigate
        to={`/user/sessions/group/${contentId}${query ? `?${query}` : ""}`}
      />
    );
  } else if (pathname.startsWith("/content-list") && contentType) {
    if (contentType === ContentTypes.RECORDING) {
      return (
        <Navigate
          to={`/user/discover?${UrlSearchParams.Tab}=${DiscoverTabs.SESSIONS}${
            query ? `&${query}` : ""
          }`}
          replace
        />
      );
    } else if (contentType === ContentTypes.LIVE_SESSION) {
      return (
        <Navigate
          to={`/user/discover?${UrlSearchParams.Tab}=${
            DiscoverTabs.LIVE_SCHEDULE
          }${query ? `&${query}` : ""}`}
          replace
        />
      );
    } else if (contentType === ContentTypes.ARTICLE) {
      return (
        <Navigate
          to={`/user/discover?${UrlSearchParams.Tab}=${
            DiscoverTabs.QUICK_READS
          }${query ? `&${query}` : ""}`}
          replace
        />
      );
    }
  } else if (
    pathname.startsWith("/content-detail") &&
    contentType &&
    contentId !== undefined
  ) {
    if (contentType === ContentTypes.RECORDING) {
      return (
        <Navigate
          to={`/user/recordings/${contentId}${query ? `?${query}` : ""}`}
          replace
        />
      );
    } else if (contentType === ContentTypes.LIVE_SESSION) {
      return (
        <Navigate
          to={`/user/sessions/group/${contentId}${query ? `?${query}` : ""}`}
          replace
        />
      );
    } else if (contentType === ContentTypes.ARTICLE) {
      return (
        <Navigate
          to={`/user/articles/${contentId}${query ? `?${query}` : ""}`}
          replace
        />
      );
    }
  } else if (authed && pathname === "/user/sessions") {
    return (
      <Navigate
        to={`/user/discover?${UrlSearchParams.Tab}=${
          DiscoverTabs.LIVE_SCHEDULE
        }${query ? `&${query}` : ""}`}
        replace
      />
    );
  } else if (authed && pathname === "/user/recordings") {
    return (
      <Navigate
        to={`/user/discover?${UrlSearchParams.Tab}=${DiscoverTabs.SESSIONS}${
          query ? `&${query}` : ""
        }`}
        replace
      />
    );
  } else if (authed && pathname === "/user/articles") {
    return (
      <Navigate
        to={`/user/discover?${UrlSearchParams.Tab}=${DiscoverTabs.QUICK_READS}${
          query ? `&${query}` : ""
        }`}
        replace
      />
    );
  } else if (authed && pathname === "/user/practice") {
    return (
      <Navigate to={`/user/discover${query ? `?${query}` : ""}`} replace />
    );
  } else if (authed && pathname === "/user/schedule") {
    return (
      <Navigate
        to={`/user/discover?${UrlSearchParams.Tab}=${
          DiscoverTabs.LIVE_SCHEDULE
        }${query ? `&${query}` : ""}`}
        replace
      />
    );
  } else if (authed && AUTHED_REDIRECT_FROM.has(pathname)) {
    return (
      <Navigate
        to={`/${query ? `?${query}` : ""}`}
        state={{ from: location }}
      />
    );
  } else {
    return (
      <Routes>
        <Route
          path="/login"
          exact
          element={
            <PageProvider title={"Sign In"}>
              <SignIn />
            </PageProvider>
          }
        />
        <Route
          path="/signin"
          exact
          element={
            <PageProvider title={"Sign In"}>
              <SignIn />
            </PageProvider>
          }
        />
        {/* Password sign-in for accounts with the password_auth flag;
            reached from the method chooser / single-method auto-pick. */}
        <Route
          path="/signin/email"
          element={
            <PageProvider title={"Sign in"}>
              <PasswordScreen />
            </PageProvider>
          }
        />
        <Route
          path="/auth/callback"
          exact
          element={
            <PageProvider title={"Signing in"}>
              <AuthCallback />
            </PageProvider>
          }
        />
        <Route
          path="/email-entry"
          exact
          element={
            <PageProvider title={"Sign in or sign up"}>
              <EmailEntry />
            </PageProvider>
          }
        />
        <Route
          path="/method-chooser"
          exact
          element={
            <PageProvider title={"Choose a sign-in method"}>
              <MethodChooser />
            </PageProvider>
          }
        />
        <Route
          path="/signin/email-otp/code"
          exact
          element={
            <PageProvider title={"Sign in"}>
              <SignInOtpCode />
            </PageProvider>
          }
        />
        <Route
          path="/signup/email-otp/code"
          exact
          element={
            <PageProvider title={"Create your account"}>
              <SignUpOtpCode />
            </PageProvider>
          }
        />
        {/* WONE-589: magic-link request screen retired (hidden from
            the chooser via HIDDEN_METHODS; WONE-604 will delete the
            backend endpoint). Stale links land on the unified entry. */}
        <Route
          path="/signin/magiclink"
          element={<Navigate to="/email-entry" replace />}
        />
        <Route
          path="/signin/not-eligable"
          element={<Navigate to="/email-entry" replace />}
        />

        <Route
          path="/signin/autostore"
          exact
          element={
            <PageProvider title={"sign-in"}>
              <AutoStore />
            </PageProvider>
          }
        />
        {/* WONE-589: password reset / set / signup-via-password
            screens retired (password sign-in itself lives at
            /signin/email). Old emailed links
            (carrying ?token=…) drop the token on the way through. */}
        <Route
          path="/signin/create"
          element={<Navigate to="/email-entry" replace />}
        />
        <Route
          path="/signin/create-account"
          element={<Navigate to="/email-entry" replace />}
        />
        <Route
          path="/signin/reset"
          element={<Navigate to="/email-entry" replace />}
        />
        <Route
          path="/signup"
          exact
          element={
            <PageProvider title={"Sign Up"}>
              <SignUp />
            </PageProvider>
          }
        />
        {
          <Route
            path="/signup/company"
            exact
            element={
              <PageProvider title={"Signup"}>
                <SignUpViaCompanyMagicLink />
              </PageProvider>
            }
          />
        }
        <Route
          path="/signup/company/invalid-link"
          exact
          element={
            <PageProvider title={"Signup"}>
              <InvalidLink />
            </PageProvider>
          }
        />
        <Route
          path="/user/onboard"
          element={
            <PageProvider title={"Onboard"}>
              <Onboard />
            </PageProvider>
          }
        />
        <Route
          path="/user/onboard/wone-profile"
          element={
            <PageProvider title={"Onboard"}>
              <Profile />
            </PageProvider>
          }
        />
        <Route
          path="/user/onboard/calendar-invites"
          element={
            <PageProvider title={"Onboard"}>
              <CalendarInvites />
            </PageProvider>
          }
        />
        <Route
          path="/user/onboard/error"
          element={
            <PageProvider title={"Onboard"}>
              <Results />
            </PageProvider>
          }
        />
        <Route
          path="/user/onboard/success"
          element={
            <PageProvider title={"Onboard"}>
              <OnboardingSuccess />
            </PageProvider>
          }
        />
        <Route
          path="/get-started"
          element={
            <PageProvider title={"Get Started"}>
              <GetStarted />
            </PageProvider>
          }
        />
        {/* WONE-589: keep /getstarted as a redirect for old bookmarks
            that pre-date the rename to /get-started. */}
        <Route
          path="/getstarted"
          element={<Navigate to="/get-started" replace />}
        />
        <Route
          path="/user/woneindex/assessment"
          element={
            <AssessmentProvider>
              <Assessment />
            </AssessmentProvider>
          }
        />
        <Route
          path="/user/woneindex/assessment/interstitial-animation"
          element={
            <AssessmentProvider>
              <AssessmentInterstitialAnimation />
            </AssessmentProvider>
          }
        />
        <Route
          path="/user/woneindex/assessment/error"
          element={
            <AssessmentProvider>
              <AssessmentSubmissionError />
            </AssessmentProvider>
          }
        />
        <Route path="/user/woneindex/report" element={<AssessmentReport />} />
        <Route path="/user/*" element={<Users />} />
        <Route
          path="/client/*"
          element={
            companyAdmin ? (
              <Corporate />
            ) : (
              <Navigate to={`/${query ? `?${query}` : ""}`} />
            )
          }
        />
        <Route
          path="/*"
          element={<Navigate to={`/${query ? `?${query}` : ""}`} />}
        />
      </Routes>
    );
  }
};

export default UserApp;
